/**
 * 63.5 step 1 (ARCHITECTURE §5.13 "63.5, the design"): the ground's material under a point — one query, read by nothing
 * yet. The address is the mesh facet under the point (surface.ts names it as it finds the height); the material is derived,
 * never stored: the facet's surface layer by its generated height (PLANET_LAYERS, normalised against the generation-time
 * range as the colouring is), its stratum by how deep it has been dug below the generated ground (PLANET_STRATA), rock on a
 * steep face (the layer profile's rockSlope, the hard threshold the facet colouring uses), and mud where rain falls on soil
 * (the weather timeline, per segment). Cached per heightmap identity, so a crater (a new heightmap array) re-derives only
 * the facets asked for again. Pure; Math.sqrt alone (exact), no transcendental.
 */
import type { GameConfig, GameState, PlanetState, Vec3 } from '../types';
import type { GroundMaterial } from '../types/ground';
import { PLANET_LAYERS, PLANET_STRATA } from '../constants';
import { LAYER_MATERIAL, STRATUM_MATERIAL } from '../constants/ground';
import { getSurfaceRadius, lastSurfaceTriangle, nearestSurfaceVertex } from './surface';
import { weatherHere, planetWeather } from './weather';
import { clockPolicyFor } from '../match/clockPolicy';

/** The generation-time height range a planet's surface bands are measured against (the colouring's rule). */
const rangeMemo = new WeakMap<Float32Array, { min: number; range: number }>();
export function generatedRange(initial: Float32Array): { min: number; range: number } {
  let r = rangeMemo.get(initial);
  if (r) return r;
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < initial.length; i++) { const h = initial[i]; if (h < min) min = h; if (h > max) max = h; }
  r = { min, range: max - min || 1 };
  rangeMemo.set(initial, r);
  return r;
}

/** A facet's slope as the colouring measures it: 1 − |n · ĉ| for its normal n and centre direction ĉ (0 flat, 1 a wall). */
export function facetSlope(bp: Float32Array, hm: Float32Array, t: number): number {
  const p = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  let cx = 0, cy = 0, cz = 0;
  for (let k = 0; k < 3; k++) {
    const vi = t * 3 + k;
    const bx = bp[vi * 3], by = bp[vi * 3 + 1], bz = bp[vi * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const s = 1 + hm[vi] / len;
    p[k * 3] = bx * s; p[k * 3 + 1] = by * s; p[k * 3 + 2] = bz * s;
    cx += p[k * 3]; cy += p[k * 3 + 1]; cz += p[k * 3 + 2];
  }
  const e1x = p[3] - p[0], e1y = p[4] - p[1], e1z = p[5] - p[2];
  const e2x = p[6] - p[0], e2y = p[7] - p[1], e2z = p[8] - p[2];
  let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  nx /= nl; ny /= nl; nz /= nl;
  const cl = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
  return 1 - Math.min(1, Math.abs((nx * cx + ny * cy + nz * cz) / cl));
}

const DUG_EPS = 1e-6;

/** The band of `bands` (ascending upTo, the last 1) that `h` falls in: the first with h ≤ upTo, else the last. */
function bandIndex(bands: ReadonlyArray<{ upTo: number }>, h: number): number {
  for (let k = 0; k < bands.length; k++) if (h <= bands[k].upTo) return k;
  return bands.length - 1;
}

/** The material of facet `t` of a planet, dry (no weather): the surface layer, or the stratum where dug, rock where steep. */
export function facetMaterial(planet: PlanetState, t: number): GroundMaterial {
  const hm = planet.heightmap, bp = planet.basePositions;
  const initial = planet.initialHeightmap ?? hm;
  const key = (planet.palette?.name === 'earth' ? 'earth' : planet.archetype) as keyof typeof PLANET_LAYERS;
  const profile = PLANET_LAYERS[key] ?? PLANET_LAYERS.thalia;
  const strata = PLANET_STRATA[key] ?? PLANET_STRATA.thalia;
  const names = LAYER_MATERIAL[key] ?? LAYER_MATERIAL.thalia;
  const i0 = t * 3;
  // the facet's generated height and how far it has been dug, as the mean of its three vertices
  const hGen = (initial[i0] + initial[i0 + 1] + initial[i0 + 2]) / 3;
  const hNow = (hm[i0] + hm[i0 + 1] + hm[i0 + 2]) / 3;
  const dug = hGen - hNow;
  let material: GroundMaterial;
  if (dug > DUG_EPS) {
    // below the generated surface: the stratum at this depth (a fraction of the radius, as the colouring reads it)
    const d = Math.max(0, Math.min(1, dug / planet.radius));
    material = STRATUM_MATERIAL[strata[bandIndex(strata, d)].name] ?? 'rock';
  } else {
    // 74.13 step 4 (374 (5)): the landforms' talus — loose rock, steep or not (it lies at the talus grade under the faces)
    if (planet.screeFacets?.[t]) return 'scree';
    const { min, range } = generatedRange(initial);
    const h = Math.max(0, Math.min(1, (hGen - min) / range));
    material = names[profile.layers[bandIndex(profile.layers, h)].name] ?? 'soil';
  }
  // a steep face is rock, at the hard threshold the facet colouring uses (the middle of rockSlope)
  if (facetSlope(bp, hm, t) >= (profile.rockSlope[0] + profile.rockSlope[1]) / 2) material = 'rock';
  return material;
}

/**
 * 63.5 step 4: how deep into its surface band facet `t` lies — 0 at the band's lower edge, 1 at its top (the snow is
 * thin where its band begins and deepest toward the peaks); 1 where the facet is dug below the generated ground.
 */
export function facetLayerDepth(planet: PlanetState, t: number): number {
  const hm = planet.heightmap, initial = planet.initialHeightmap ?? hm;
  const key = (planet.palette?.name === 'earth' ? 'earth' : planet.archetype) as keyof typeof PLANET_LAYERS;
  const profile = PLANET_LAYERS[key] ?? PLANET_LAYERS.thalia;
  const i0 = t * 3;
  const hGen = (initial[i0] + initial[i0 + 1] + initial[i0 + 2]) / 3;
  if (hGen - (hm[i0] + hm[i0 + 1] + hm[i0 + 2]) / 3 > DUG_EPS) return 1;
  const { min, range } = generatedRange(initial);
  const h = Math.max(0, Math.min(1, (hGen - min) / range));
  const k = bandIndex(profile.layers, h);
  const lo = k > 0 ? profile.layers[k - 1].upTo : 0, hi = profile.layers[k].upTo;
  return hi > lo ? Math.max(0, Math.min(1, (h - lo) / (hi - lo))) : 1;
}

/** Per heightmap identity: the material of each facet asked for (0 = not yet derived; else 1 + its index in GROUND_MATERIALS). */
const facetMemo = new WeakMap<Float32Array, Uint8Array>();
const ORDER: GroundMaterial[] = ['soil', 'rock', 'sand', 'snow', 'ice', 'mud', 'ash', 'salt', 'scree'];

/** The dry material of facet `t`, memoised per heightmap (a crater is a new heightmap: its facets are derived afresh when asked). */
export function facetMaterialCached(planet: PlanetState, t: number): GroundMaterial {
  const hm = planet.heightmap;
  let memo = facetMemo.get(hm);
  if (!memo) { memo = new Uint8Array(Math.max(1, Math.floor(hm.length / 3))); facetMemo.set(hm, memo); }
  if (t < 0 || t >= memo.length) return facetMaterial(planet, Math.max(0, Math.min(memo.length - 1, t)));
  const have = memo[t];
  if (have) return ORDER[have - 1];
  const m = facetMaterial(planet, t);
  memo[t] = ORDER.indexOf(m) + 1;
  return m;
}

/** The facet under a direction: the one the surface lookup hits, or the nearest vertex's where the lookup fell back. */
export function facetUnder(planet: PlanetState, dir: Vec3): number {
  getSurfaceRadius(planet, dir);
  const t = lastSurfaceTriangle();
  return t >= 0 ? t : Math.floor(nearestSurfaceVertex(planet, dir) / 3);
}

/** The ground's dry material under a direction on a planet (no weather). */
export function groundMaterialAt(planet: PlanetState, dir: Vec3): GroundMaterial {
  return facetMaterialCached(planet, facetUnder(planet, dir));
}

/**
 * The ground's material under a direction now: the dry material, and mud where the weather timeline has rain or a storm
 * over soil this segment (62.19 (e)). The one query the readers will take (craters, damage, walking, footsteps, the look).
 */
export function groundMaterial(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, dir: Vec3, actionSeconds = 0): GroundMaterial {
  const planet = state.planets[planetIndex];
  if (!planet) return 'soil';
  const dry = groundMaterialAt(planet, dir);
  if (dry !== 'soil') return dry;
  const w = weatherHere(state, planetIndex, dir, actionSeconds);   // 62.54: the rain where this ground is
  return w === 'rain' || w === 'storm' ? 'mud' : dry;
}

/** 63.5 step 6 (decision 155): does this match's ground have materials? The clock's policy decides (the action clock's does; the turn game's is plain). */
export function groundMaterialsOn(config: Pick<GameConfig, 'clock'>): boolean {
  return clockPolicyFor(config).ground === 'materials';
}
